import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardHeader, CardContent, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Accordion, AccordionSummary, AccordionDetails, LinearProgress
} from '@mui/material';
import { Plus, Edit2, Trash2, Cloud, TestTube, ChevronDown, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../api';
import ConfirmModal from '../ConfirmModal';

export default function OPCManagement() {
  const [opcServers, setOpcServers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteServerId, setDeleteServerId] = useState(null);

  const [formData, setFormData] = useState({
    serverName: '',
    progId: '',
    serverAddress: 'localhost',
    updateRate: 1000,
    groupName: '',
    isActive: true,
    opcVersion: 'DA',
    username: '',
    password: '',
    items: []
  });

  useEffect(() => {
    loadOpcServers();
  }, []);

  const loadOpcServers = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API call
      const mockServers = [
        {
          id: 1,
          serverName: 'SCADA OPC Server',
          progId: 'Matrikon.OPC.Simulation.1',
          serverAddress: 'localhost',
          updateRate: 1000,
          groupName: 'Axis_Group',
          isActive: true,
          opcVersion: 'DA',
          connectionStatus: 'connected',
          itemCount: 12,
          lastUpdate: new Date().toLocaleString(),
          items: [
            { itemId: 'Random.Int4', clientHandle: 1, dataType: 'VT_I4', value: '42' },
            { itemId: 'Random.Real8', clientHandle: 2, dataType: 'VT_R8', value: '3.14159' },
            { itemId: 'Random.Boolean', clientHandle: 3, dataType: 'VT_BOOL', value: 'true' }
          ]
        },
        {
          id: 2,
          serverName: 'Production OPC UA',
          progId: '',
          serverAddress: '192.168.1.50:4840',
          updateRate: 500,
          groupName: 'Production_Data',
          isActive: false,
          opcVersion: 'UA',
          connectionStatus: 'disconnected',
          itemCount: 0,
          lastUpdate: new Date().toLocaleString(),
          items: []
        }
      ];
      setOpcServers(mockServers);
    } catch (error) {
      console.error('Error loading OPC servers:', error);
      toast.error('OPC servers yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (server = null) => {
    if (server) {
      setSelectedServer(server);
      setFormData({
        serverName: server.serverName,
        progId: server.progId,
        serverAddress: server.serverAddress,
        updateRate: server.updateRate,
        groupName: server.groupName,
        isActive: server.isActive,
        opcVersion: server.opcVersion,
        username: server.username || '',
        password: server.password || '',
        items: server.items || []
      });
    } else {
      setSelectedServer(null);
      setFormData({
        serverName: '',
        progId: '',
        serverAddress: 'localhost',
        updateRate: 1000,
        groupName: '',
        isActive: true,
        opcVersion: 'DA',
        username: '',
        password: '',
        items: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedServer(null);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      // Mock connection test
      await new Promise(resolve => setTimeout(resolve, 2000));
      const success = Math.random() > 0.3; // 70% success rate
      
      if (success) {
        toast.success('OPC connection test successful');
      } else {
        toast.error('OPC connection test failed');
      }
    } catch (error) {
      console.error('Error testing OPC connection:', error);
      toast.error('Connection test hatası');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    try {
      if (selectedServer) {
        // Update existing server
        const updatedServer = { 
          ...selectedServer, 
          ...formData,
          lastUpdate: new Date().toLocaleString()
        };
        setOpcServers(opcServers.map(s => s.id === selectedServer.id ? updatedServer : s));
        toast.success('OPC server başarıyla güncellendi');
      } else {
        // Create new server
        const newServer = {
          id: Math.max(...opcServers.map(s => s.id), 0) + 1,
          ...formData,
          connectionStatus: 'disconnected',
          itemCount: 0,
          lastUpdate: new Date().toLocaleString()
        };
        setOpcServers([...opcServers, newServer]);
        toast.success('OPC server başarıyla eklendi');
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving OPC server:', error);
      toast.error('OPC server kaydedilirken hata oluştu');
    }
  };

  const handleDelete = (serverId) => {
    setDeleteServerId(serverId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setOpcServers(opcServers.filter(s => s.id !== deleteServerId));
      toast.success('OPC server başarıyla silindi');
    } catch (error) {
      console.error('Error deleting OPC server:', error);
      toast.error('OPC server silinirken hata oluştu');
    }
  };

  const getStatusChip = (server) => {
    if (!server.isActive) {
      return <Chip label="Inactive" size="small" color="default" />;
    }
    
    switch (server.connectionStatus) {
      case 'connected':
        return <Chip label="Connected" size="small" color="success" icon={<Wifi size={14} />} />;
      case 'connecting':
        return <Chip label="Connecting" size="small" color="warning" />;
      case 'disconnected':
      default:
        return <Chip label="Disconnected" size="small" color="error" icon={<WifiOff size={14} />} />;
    }
  };

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">OPC Servers</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={`${opcServers.length} Servers`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
                <Button 
                  variant="contained" 
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenDialog()}
                  disabled={loading}
                >
                  Add OPC Server
                </Button>
              </Box>
            </Box>
          }
        />
        
        <CardContent sx={{ flex: 1, pt: 1, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {opcServers.map((server) => (
              <Accordion key={server.id}>
                <AccordionSummary expandIcon={<ChevronDown />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Cloud size={20} />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {server.serverName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {server.opcVersion === 'DA' ? server.progId : server.serverAddress}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusChip(server)}
                      <Chip 
                        label={`${server.itemCount} items`} 
                        size="small" 
                        variant="outlined" 
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Server Details
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(server)}
                          color="primary"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(server.id)}
                          color="error"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Typography variant="body2">
                        <strong>Version:</strong> OPC {server.opcVersion}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Update Rate:</strong> {server.updateRate}ms
                      </Typography>
                      <Typography variant="body2">
                        <strong>Group:</strong> {server.groupName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Last Update:</strong> {server.lastUpdate}
                      </Typography>
                    </Box>

                    {server.items && server.items.length > 0 && (
                      <TableContainer sx={{ mt: 2, maxHeight: 200 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Item ID</TableCell>
                              <TableCell>Data Type</TableCell>
                              <TableCell>Current Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {server.items.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography variant="body2" fontFamily="monospace">
                                    {item.itemId}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={item.dataType} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                    {item.value || 'N/A'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {opcServers.length === 0 && !loading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: 300, 
              color: 'text.secondary' 
            }}>
              <Cloud size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography variant="h6">No OPC Servers Configured</Typography>
              <Typography variant="body2">Add an OPC DA or OPC UA server to get started</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* OPC Server Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedServer ? 'Edit OPC Server' : 'Add OPC Server'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Server Name"
              value={formData.serverName}
              onChange={(e) => setFormData({ ...formData, serverName: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>OPC Version</InputLabel>
              <Select
                value={formData.opcVersion}
                onChange={(e) => setFormData({ ...formData, opcVersion: e.target.value })}
                label="OPC Version"
              >
                <MenuItem value="DA">OPC DA (Classic)</MenuItem>
                <MenuItem value="UA">OPC UA</MenuItem>
              </Select>
            </FormControl>

            {formData.opcVersion === 'DA' ? (
              <TextField
                label="Program ID (ProgID)"
                value={formData.progId}
                onChange={(e) => setFormData({ ...formData, progId: e.target.value })}
                fullWidth
                required
                placeholder="Matrikon.OPC.Simulation.1"
                helperText="OPC Server ProgID from registry"
              />
            ) : (
              <TextField
                label="Server Address"
                value={formData.serverAddress}
                onChange={(e) => setFormData({ ...formData, serverAddress: e.target.value })}
                fullWidth
                required
                placeholder="opc.tcp://localhost:4840"
                helperText="OPC UA endpoint URL"
              />
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Update Rate (ms)"
                type="number"
                value={formData.updateRate}
                onChange={(e) => setFormData({ ...formData, updateRate: parseInt(e.target.value) || 1000 })}
                fullWidth
              />
              <TextField
                label="Group Name"
                value={formData.groupName}
                onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                fullWidth
                placeholder="Axis_Group"
              />
            </Box>

            {formData.opcVersion === 'UA' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  fullWidth
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<TestTube size={16} />}
                onClick={handleTestConnection}
                disabled={testingConnection || !formData.serverName}
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
              {testingConnection && <LinearProgress sx={{ flex: 1 }} />}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.serverName || (!formData.progId && !formData.serverAddress)}
          >
            {selectedServer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete OPC Server"
        content="Are you sure you want to delete this OPC server? This action cannot be undone."
        type="yesNo"
      />
    </Box>
  );
}